import { useEffect, useState, type RefObject } from "react";

/**
 * Which answer the artifact pane should mirror (PRD FR-6.7).
 *
 * "The answer nearest the viewport" needs a definition, and this is it: the
 * turn straddling a line about a third of the way down the conversation, or
 * failing that the nearest one to it. A third rather than the top because a
 * reader scrolls an answer *into* view before reading it, and the pane should
 * change as the answer arrives rather than after it has gone past.
 */
export function useScrollLink(
  container: RefObject<HTMLElement | null>,
  ids: string[],
): string | null {
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    const element = container.current;
    if (!element || ids.length === 0) {
      setSelected(null);
      return;
    }

    let frame = 0;
    const measure = () => {
      frame = 0;
      const bounds = element.getBoundingClientRect();
      const anchor = bounds.top + element.clientHeight * 0.33;
      let best: { id: string; distance: number } | null = null;
      for (const id of ids) {
        const node = element.querySelector(`[data-message-id="${CSS.escape(id)}"]`);
        if (!node) continue;
        const rect = node.getBoundingClientRect();
        const distance =
          rect.top <= anchor && rect.bottom >= anchor
            ? 0
            : Math.min(Math.abs(rect.top - anchor), Math.abs(rect.bottom - anchor));
        if (best === null || distance < best.distance) best = { id, distance };
      }
      setSelected(best?.id ?? ids[ids.length - 1] ?? null);
    };

    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };

    measure();
    element.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      element.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [container, ids]);

  return selected;
}
